<section #workSection id="work" class="relative scroll-mt-28 overflow-hidden border-t border-surface bg-surface-50 dark:bg-white/5" [class.pipeline-paused]="!isVisible()">
    <app-landing-glow [variant]="2" />
    <div class="relative z-10 mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div class="max-w-3xl">
            <div class="text-sm font-semibold uppercase tracking-[0.3em] text-primary">{{ text().workTitle }}</div>
            <h2 class="mt-4 text-3xl font-semibold text-surface-900 sm:text-4xl dark:text-white">{{ text().workSubtitle }}</h2>
        </div>

        <div class="mt-10 grid gap-5 lg:grid-cols-3">
            @for (card of text().workCards; track card.title; let i = $index) {
                <article
                    [appRevealOnScroll]="i * 90"
                    class="rounded-3xl border border-surface bg-surface-0 p-6 shadow-lg shadow-black/10 backdrop-blur-sm transition dark:bg-white/5"
                    [class.pipeline-stage-card--active]="hoveredStage() === i"
                    (mouseenter)="hoveredStage.set(i)"
                    (mouseleave)="hoveredStage.set(null)"
                >
                    <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100 dark:bg-primary-400/10 text-primary text-xl">
                        <i [class]="'pi ' + workIcons[i]"></i>
                    </div>
                    <h3 class="mt-5 text-2xl font-semibold text-surface-900 dark:text-white">{{ card.title }}</h3>
                    <p class="mt-3 leading-7 text-muted-color">{{ card.description }}</p>
                </article>
            }
        </div>

        <!-- Product-screenshot style mock of a single invoice moving through the pipeline; hover a card above to spotlight its stage. -->
        <div class="pipeline-mock mt-10 rounded-[2rem] border border-white/10 bg-slate-900 p-6 shadow-2xl shadow-black/30 sm:p-8">
            <div class="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-5">
                <div class="flex items-center gap-2">
                    <span class="text-sm font-semibold text-white sm:text-base">{{ text().pipelineMock.heading }}</span>
                    <span class="pipeline-mock-dot"></span>
                </div>
                <span class="pipeline-mock-live-badge">
                    <span class="pipeline-mock-live-dot"></span>
                    {{ text().pipelineMock.liveLabel }}
                </span>
            </div>

            <div class="mt-8 flex flex-col items-stretch gap-4 md:flex-row md:items-stretch">
                <div class="pipeline-mock-column flex flex-1 flex-col items-center gap-3" [class.pipeline-mock-column--active]="hoveredStage() === 0">
                    <span class="pipeline-mock-label">{{ text().pipelineMock.invoiceLabel }}</span>
                    <!-- Any intake channel lands the same way: scanned, mailed, messaged, or dropped in a shared drive. -->
                    <div class="pipeline-mock-channels">
                        <span class="pipeline-mock-channel" [title]="text().pipelineMock.scanLabel" [attr.aria-label]="text().pipelineMock.scanLabel"><i class="pi pi-camera text-sky-400"></i></span>
                        <span class="pipeline-mock-channel" [title]="text().pipelineMock.emailLabel" [attr.aria-label]="text().pipelineMock.emailLabel"><i class="pi pi-envelope text-amber-400"></i></span>
                        <span class="pipeline-mock-channel" [title]="text().pipelineMock.whatsappLabel" [attr.aria-label]="text().pipelineMock.whatsappLabel"><i class="pi pi-whatsapp text-green-400"></i></span>
                    </div>
                    <div class="pipeline-mock-card flex w-full flex-1 flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div class="flex items-center gap-2 text-sm font-medium text-white">
                            <i class="pi pi-file-pdf text-red-400"></i>
                            <span>{{ text().pipelineMock.invoiceFileName }}</span>
                        </div>
                        <div class="pipeline-mock-skeleton">
                            <span class="pipeline-mock-bar" style="width: 92%"></span>
                            <span class="pipeline-mock-bar" style="width: 78%"></span>
                            <span class="pipeline-mock-bar" style="width: 85%"></span>
                            <span class="pipeline-mock-bar" style="width: 60%"></span>
                        </div>
                    </div>
                </div>

                <i class="pi pi-arrow-right pipeline-mock-arrow rotate-90 self-center md:rotate-0"></i>

                <div class="pipeline-mock-column flex flex-1 flex-col items-center gap-3" [class.pipeline-mock-column--active]="hoveredStage() === 1">
                    <span class="pipeline-mock-label">{{ text().pipelineMock.aiAnalysisLabel }}</span>
                    <div class="pipeline-mock-card flex w-full flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
                        <span class="pipeline-mock-spinner"></span>
                        <p class="text-sm font-semibold text-blue-400">{{ text().pipelineMock.extractingLabel }}</p>
                        <p class="text-xs text-slate-400">{{ text().pipelineMock.fieldsLabel }}</p>
                    </div>
                </div>

                <i class="pi pi-arrow-right pipeline-mock-arrow rotate-90 self-center md:rotate-0"></i>

                <div class="pipeline-mock-column flex flex-1 flex-col items-center gap-3" [class.pipeline-mock-column--active]="hoveredStage() === 2">
                    <span class="pipeline-mock-label">{{ text().pipelineMock.accountingSystemLabel }}</span>
                    <div class="pipeline-mock-card flex w-full flex-1 flex-col justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div class="flex items-center justify-between gap-2">
                            <span class="text-xs text-slate-400">{{ text().pipelineMock.supplierLabel }}</span>
                            <span class="text-sm font-medium text-white">{{ text().pipelineMock.supplierValue }}</span>
                        </div>
                        <div class="flex items-center justify-between gap-2 border-t border-white/10 pt-3">
                            <span class="text-xs text-slate-400">{{ text().pipelineMock.totalDueLabel }}</span>
                            <span class="text-sm font-semibold text-white">₪4,215.00</span>
                        </div>
                    </div>
                    <span class="pipeline-mock-received">
                        <i class="pi pi-check"></i>
                        {{ text().pipelineMock.receivedLabel }}
                    </span>
                </div>
            </div>

            <div class="pipeline-mock-breadcrumb mt-8 flex flex-wrap items-center justify-center gap-2 border-t border-white/10 pt-5 text-xs text-slate-400">
                <span>{{ text().pipelineMock.breadcrumbExtraction }}</span>
                <i class="pi pi-arrow-right pipeline-mock-arrow"></i>
                <span>{{ text().pipelineMock.breadcrumbAnalysis }}</span>
                <i class="pi pi-arrow-right pipeline-mock-arrow"></i>
                <span>{{ text().pipelineMock.breadcrumbAccounting }}</span>
            </div>
        </div>
    </div>
</section>
